import * as React from "react";
import { Brain, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@finster/ui";

import type { LiveThoughtProcessStatus } from "./resolveLiveThoughtProcessStatus";

export const ThoughtProcessPanel: React.FC<{
  children?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  liveStatus?: LiveThoughtProcessStatus;
}> = ({
  children,
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  liveStatus,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const hasChildren = React.Children.count(children) > 0;
  const statusLabel = liveStatus?.statusLabel;
  const showBodyStatus = liveStatus?.placement === "body";
  const showStatusPill = liveStatus?.placement === "pill";

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-2 text-left transition-colors hover:bg-muted/40"
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
          <Brain className="h-4 w-4" />
        </span>
        <span className="flex-1 text-[13px] font-medium text-foreground">
          Thought process
        </span>
        <span className="shrink-0 text-muted-foreground/70">
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              !open && "-rotate-90",
            )}
          />
        </span>
      </button>
      {open && (
        <div className="px-1.5 py-2">
          {children}
          {showBodyStatus ? (
            <div
              className={cn(
                "flex items-center gap-2 text-[12px] text-muted-foreground",
                hasChildren && "mt-3",
              )}
            >
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>{statusLabel}</span>
            </div>
          ) : null}
        </div>
      )}
      {showStatusPill ? (
        <div className="flex items-center gap-2 rounded-lg border border-primary-muted bg-primary/5 px-3 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-[12px] font-medium text-primary">{statusLabel}</span>
        </div>
      ) : null}
    </div>
  );
};
